import type { RouteRecordRaw } from 'vue-router';

const appVariant = import.meta.env.VITE_APP_VARIANT;

const appointmentManagerChildren: RouteRecordRaw[] = [
  {
    path: '',
    component: () => import('layouts/UserLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('pages/booking/BookingPage.vue') },
      {
        path: 'booking/verify/:token',
        name: 'verify-booking',
        component: () => import('pages/booking/VerifyBooking.vue'),
      },
      {
        path: 'booking/status',
        name: 'booking-status',
        component: () => import('pages/booking/RequestStatus.vue'),
      },
    ],
  },
  {
    path: 'admin',
    component: () => import('layouts/AdminLayout.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: (to) => ({ name: 'admin-calendar', params: to.params }),
      },
      {
        path: 'calendar',
        name: 'admin-calendar',
        component: () => import('pages/admin/CalendarView.vue'),
        meta: { requiresPermission: 'calendar', requiresSiteAccess: true },
      },
      {
        path: 'invoices',
        name: 'admin-invoices',
        component: () => import('pages/admin/InvoicesView.vue'),
        meta: { requiresPermission: 'lexware', requiresSiteAccess: true },
      },
      {
        path: 'integrations',
        name: 'admin-integrations',
        component: () => import('pages/admin/IntegrationsConfig.vue'),
        meta: { requiresRole: 'role:ROLE_ADMIN' },
      },
      {
        path: 'mappings',
        name: 'admin-mappings',
        component: () => import('pages/admin/MappingsView.vue'),
        meta: { requiresRole: 'role:ROLE_ADMIN', requiresSiteAccess: true },
      },
      {
        path: 'config-editor',
        name: 'admin-config-editor',
        component: () => import('pages/admin/ConfigEditorView.vue'),
        meta: { requiresRole: 'role:ROLE_ADMIN' },
      },
      {
        path: 'permissions',
        name: 'admin-permissions',
        component: () => import('pages/admin/PermissionsView.vue'),
        meta: { requiresRole: 'role:ROLE_ADMIN', requiresPermission: 'system-admin' },
      },
      {
        path: 'patients',
        name: 'admin-patients',
        component: () => import('pages/admin/PatientOverviewPage.vue'),
        meta: { requiresPermission: 'patients' },
      },
      {
        path: 'service-desk',
        name: 'admin-service-desk',
        component: () => import('pages/admin/ServiceDeskPage.vue'),
        meta: { requiresPermission: 'booking', requiresSiteAccess: true },
      },
      {
        path: 'unauthorized',
        name: 'admin-unauthorized',
        component: () => import('pages/admin/UnauthorizedView.vue'),
      },
      {
        path: 'doctolib',
        redirect: (to) => ({ name: 'admin-integrations', params: to.params }),
      },
      {
        path: 'apollo',
        redirect: (to) => ({ name: 'admin-integrations', params: to.params }),
      },
    ],
  },
  {
    path: 'admin/login',
    name: 'admin-login',
    component: () => import('pages/admin/AdminLogin.vue'),
  },
];

const prediktoChildren: RouteRecordRaw[] = [
  {
    path: '',
    component: () => import('layouts/PrediktoLayout.vue'),
    children: [
      {
        path: '',
        name: 'home',
        redirect: { name: 'predikto-selection' },
      },
      {
        path: 'selection',
        name: 'predikto-selection',
        component: () => import('pages/predikto/SelectionPage.vue'),
      },
      {
        path: 'add-ons',
        name: 'predikto-add-ons',
        component: () => import('pages/predikto/SelectionAddOnsPage.vue'),
      },
      {
        path: 'location',
        name: 'predikto-location',
        component: () => import('pages/predikto/SiteSelectionPage.vue'),
      },
      {
        path: 'appointment',
        name: 'predikto-appointment',
        component: () => import('pages/predikto/AppointmentPage.vue'),
      },
      {
        path: 'personal-data',
        name: 'predikto-personal-data',
        component: () => import('pages/predikto/PersonalDataPage.vue'),
      },
      {
        path: 'summary',
        name: 'predikto-summary',
        component: () => import('pages/predikto/SummaryPage.vue'),
      },
      {
        path: 'booking/status',
        name: 'predikto-status',
        component: () => import('pages/predikto/StatusPage.vue'),
      },
      {
        path: 'booking/verify/:token',
        name: 'predikto-verify',
        component: () => import('pages/predikto/VerifyPage.vue'),
      },
    ],
  },
];

const magazinChildren: RouteRecordRaw[] = [
  {
    path: '',
    component: () => import('layouts/MagazinLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('pages/magazin/BookingPage.vue') },
      {
        path: 'form',
        name: 'form-booking',
        component: () => import('pages/FormBookingPage.vue'),
      },
      {
        path: 'booking/verify/:token',
        name: 'verify-booking',
        component: () => import('pages/VerifyBooking.vue'),
      },
      {
        path: 'booking/status',
        name: 'booking-status',
        component: () => import('pages/RequestStatus.vue'),
      },
    ],
  },
];

const networkChildren: RouteRecordRaw[] = [
  {
    path: '',
    component: () => import('layouts/NetworkLayout.vue'),
    children: [
      { path: '', name: 'home', component: () => import('pages/network/BookingPage.vue') },
      {
        path: 'form',
        name: 'form-booking',
        component: () => import('pages/FormBookingPage.vue'),
      },
      {
        path: 'booking/verify/:token',
        name: 'verify-booking',
        component: () => import('pages/VerifyBooking.vue'),
      },
      {
        path: 'booking/status',
        name: 'booking-status',
        component: () => import('pages/RequestStatus.vue'),
      },
    ],
  },
];

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    redirect: '/de', // Default to German
  },
  {
    path: '/:lang(en|de)',
    children: [
      ...(appVariant === 'predikto'
        ? prediktoChildren
        : appVariant === 'magazin'
          ? magazinChildren
          : appVariant === 'network'
            ? networkChildren
            : appointmentManagerChildren),
      {
        // Catch-all within language slug
        path: ':catchAll(.*)*',
        name: 'not-found',
        component: () => import('pages/ErrorNotFound.vue'),
      },
    ],
  },
  {
    // Catch routes without language slug and redirect to default language
    path: '/:catchAll(.*)*',
    redirect: (to) => {
      return `/de${to.path}`;
    },
  },
];

export default routes;
